import React, {useEffect, useMemo, useState} from 'react';
import {useProject} from "../../../providers/ClientProjectProvider";
import {useAuth} from "../../../providers/AuthProvider";
import AnimatedNumber from "animated-number-react";
import {SimpleCircleProgress} from "../../../components/CircleProgress";
import {Drawer, InputNumber, Tabs, Tooltip} from "antd";
import {usePositionForm} from "../../../providers/PositionFormProvider";
import Form, {FormInputComponent} from "../../../components/form/Form";
import LoadingSpinner from "../../../components/LoadingSpinner";
import UserImageCircle from "../../../components/UserImageCircle";
import {usePosition} from "../../../providers/ProjectPositionProvider";
import ManageTalentContractForPosition from "../../client-lead-tools/ManageTalentContractForPosition";
import PositionOverrideProvider from "../../../providers/PositionOverrideProvider";
import PositionCandidateCostOverrideTab from "../../client-lead-tools/PositionCandidateCostOverrideTab";
import ChangeOrderItemView from "../../client-lead-tools/change-orders/ChangeOrderItemView";
import PositionTimeline from "../../project-position/components/PositionTimeline";
// import PositionContractExpensesTable from "../../project-position/components/PositionContractExpensesTable";
import TalentCandidate from "../../../models/TalentCandidate";
import HideTalentPricingTooltip from '../../clients/HideTalentPricingTooltip';


const typeLabels = {
    Timeline: 'Timeline Change',
    Rate: 'Rate/Cost Change',
    Candidate: 'Resource Swap'
}
const PositionEditInput = ({onEdit, isEditing, canEdit, label, displayValue, isInvalid, formClass = '', ...props}) => {

    const {
        isDisabled,
    } = usePositionForm();

    return (
        <div className="w-full flex-grow relative min-h-[71px] pt-3">
            <div className="pb-2 flex flex-row items-center gap-1.5">
                <p className="inline font-semibold text-xs text-light-gray4 whitespace-nowrap">
                    {label}
                </p>
                {canEdit && !isEditing && (
                    <button type="button" disabled={isDisabled} onClick={onEdit}
                            className="btn btn-link-primary no-underline hover:underline focus:underline py-0 disabled:opacity-60 disabled:no-underline">
                        <i className="fas fa-pen underline"/>
                    </button>
                )}

            </div>

            {(canEdit && isEditing) ? (
                <div className={"flex flex-col position-cost-form " + formClass}>

                    {props.children}

                </div>
            ) : (
                <p>{displayValue}</p>
            )}

        </div>
    )
}
const PositionStartInput = (props) => {

    const {
        isLoading,
        formModel,
        isDisabled
    } = usePositionForm();

    const startMinDate = useMemo(() => {
        let minDate = new Date();

        if (formModel.started_at && TB.moment(formModel.started_at).clone().endOf('day').toDate() < minDate) {
            return TB.moment(formModel.started_at).clone().endOf('day').toDate()
        }

        return minDate;
    }, [formModel.start_date]);

    return (
        <FormInputComponent
            fieldName="started_at"
            value={formModel.started_at}
            label={false}
            type="date"
            disabled={isDisabled}
            minDate={startMinDate}
            required={true}
            validateBeforeSubmit={false}
        />
    )
}
const PositionEndInput = (props) => {

    const {
        formModel,
        isDisabled
    } = usePositionForm();


    return (
        <FormInputComponent
            fieldName="ended_at"
            value={formModel.ended_at}
            label={false}
            type="date"
            disabled={isDisabled}
            minDate={formModel.started_at ? TB.moment(formModel.started_at)
                .clone()
                .endOf('week')
                .toDate() : undefined}
            required={true}
            validateBeforeSubmit={false}
        />
    )
}

const PositionCandidateCostTab = ({canEdit, activeEdit, setActiveEdit, ...props}) => {
    const {auth} = useAuth();
    const {project, isLoading} = useProject();

    const isFixedFee = project.isFixedFee();
    const {
        formModel,
        position,
        onFormReset,
        onFormSubmit,
        onFormChange,
        deletePosition,
        updateContractTerms,
        isProjectUpdating: isDisabled
    } = usePositionForm();

    const canEditProjectAndPositions = auth?.canEditProjectAndPositions(project);
    const canAddRemoveProjectPositions = auth?.canAddRemoveProjectPositions(project);

    const editDisabled = !canEditProjectAndPositions || project.isActive() || project.is_on_hold || (project.hasRequestedSOW() && !auth.canManageClients());

    const originalStandardMarkup = useMemo(() => {
        return Number(parseFloat(project?.percent_markup).toFixed(2) * 100)
    }, [project?.percent_markup, formModel?.percent_markup]);

    // console.log('PositionCandidateCostTab', {originalStandardMarkup, formModel})

    const candidate = useMemo(() => {
        return position.assignedCandidate();
    }, [position?.id, position.assigned_candidate,isDisabled, position?.is_filled]);


    const onEditSection = async (section) => {
        console.log('onEditSection', section, editDisabled)
        if (!canEdit || editDisabled) return;

        await onFormReset();
        setActiveEdit(section);
    }

    const onSaveSection = async (params = {}) => {
        if (!canEdit || editDisabled) return;
        let updateParams = {...formModel, ...params};
        if (activeEdit === 'display_percent_markup') {
            // await updateContractTerms({percent_markup: updateParams.percent_markup});
        }else {
            delete updateParams.percent_markup;
            delete updateParams.display_percent_markup;
        }
        await onFormSubmit(updateParams)

        setActiveEdit(null);
    }
    const hourlyRate = TB.formatCurrency(position.hasBeenFilled() ? candidate?.job_candidate?.platform_rate : position.platform_rate, false);

    const [bulkCost, setBulkCost] = useState(
        position.hasBeenFilled() ? candidate?.job_candidate?.exact_platform_cost : position.exact_platform_cost
    );


    // Calculated values
    const proposedPayoutPerPeriod = useMemo(() => {
        if (!candidate) {
            return position.calculateFixedRetainerBudgetPerPeriod()
        }
        const talentOpp = TB.first(candidate?.job_candidate?.talent_opps);
        const isRateBased = position.isFixedRetainerRateBased();

        return isRateBased ? (
                (candidate?.job_candidate?.exact_platform_rate * candidate?.job_candidate?.confirmed_avg_hours_per_period)) :
            talentOpp?.platform_cost
    }, [
        candidate,
        position?.actual_total_periods,
        position?.target_rate,
        candidate?.job_candidate?.hourly_rate,
        candidate?.job_candidate?.confirmed_avg_hours_per_period,
        candidate?.job_candidate?.payout_amount,
    ]);
    const [duration, setDuration] = useState(0);

    useEffect(() => {
        setDuration(1000);
        setBulkCost(position.hasBeenFilled() ? candidate?.job_candidate?.exact_platform_cost : position.exact_platform_cost);
    }, [candidate?.job_candidate?.exact_platform_cost, position.exact_platform_cost, position.actual_total_periods, position.avg_hours_per_period, position.started_at, position.ended_at]);

    let displayedHoursPerWeek = position.assigned_candidate?.job_candidate?.confirmed_avg_hours_per_period || position.avg_hours_per_period
    let displayedTotalHours = position.assigned_candidate?.job_candidate?.total_hours || position.actual_total_periods;
    if (position.isFixedRetainer() && position.isFixedRetainerRateBased()) {
        displayedTotalHours = parseFloat(position.actual_total_periods) * displayedHoursPerWeek
    }


    return (
        <React.Fragment>
            <Form
                // ref={formRef}
                className={"position-cost-form-container"}
                onSubmit={() => onSaveSection()}
                submitButton={false}
                background={"none"}
                isDisabled={isDisabled}
                model={formModel}
                onChange={params => {
                    if (editDisabled) return;

                    let update = {...formModel}
                    for (let [key, value] of Object.entries(params)) {
                        update = TB.set(update, key, value);
                    }
                    // Reset end date if start date changes moves ahead
                    if (update.ended_at || update.started_at) {
                        let updatedEndDate = TB.moment(update.ended_at);
                        let currentStartDate = TB.moment(update.started_at || formModel.started_at);
                        if (!updatedEndDate.isValid() || TB.moment(currentStartDate)
                            .isSameOrAfter(updatedEndDate, 'day')) {
                            update.ended_at = null;
                        }
                        if (position.isFixedRetainer()) {
                            update.ended_at = position.calculateFixedRetainedEstimatedEndDate(update.started_at);
                        }
                    }
                    // if (params.display_percent_markup && formModel.display_percent_markup !== params.display_percent_markup) {
                    //     update.percent_markup = Number(parseFloat(params.display_percent_markup) / 100);
                    //     console.log('onFormChange', params.display_percent_markup)
                    // }
                    // update.display_percent_markup = Number((parseFloat((update.percent_markup)) * 100).toFixed(6));

                    onFormChange(TalentCandidate.make(update))
                }}
                fields={[]}>
                <div className="flex flex-col md:flex-row md:flex-wrap gap-3 sm:gap-5 items-stretch xs:items-start mb-3">

                    <div className="flex xs:flex-col flex-1">

                        <PositionEditInput
                            label="Start Date"
                            formClass="position-dates"
                            isInvalid={!formModel.started_at || !TB.moment(formModel.started_at).isValid()}
                            displayValue={position.startedAt().format("MMM Do, YYYY")}
                            canEdit={canEdit && !editDisabled}
                            onEdit={() => onEditSection('timing_start')}
                            isEditing={activeEdit && activeEdit !== 'display_percent_markup'}
                        >
                            <PositionStartInput/>
                        </PositionEditInput>

                        {project.isTimeMaterials() ? (
                            <PositionEditInput
                                label={position.hasBeenFilled() ? "Estimated Hours Per Week" : "Hours per Week"}
                                isInvalid={!formModel.avg_hours_per_period}
                                displayValue={`${displayedHoursPerWeek} ${TB.pluralize('hr', parseInt(displayedHoursPerWeek))} / week`}
                                canEdit={canEdit && !editDisabled}
                                onEdit={() => onEditSection('hours_per_week')}
                                isEditing={activeEdit && activeEdit !== 'display_percent_markup'}
                            >
                                <FormInputComponent
                                    fieldName="avg_hours_per_period"
                                    value={position.avg_hours_per_period}
                                    label={false}
                                    type="antd-input"
                                    disabled={isDisabled}
                                    max={40}
                                    pattern={/^\b([1-9]|[1-3][0-9]|40)\b/}
                                    required={true}
                                    min={1}
                                    validation={{
                                        rules: {
                                            isInt: true,
                                            matchRegexp: /^\b([1-9]|[1-3][0-9]|40)\b/,
                                        },
                                        errors: {
                                            matchRegexp: "The average number of hours must be between 1 and 40",
                                        }
                                    }}
                                >

                                    <InputNumber
                                        controls={false}
                                        className="w-full"
                                        addonAfter={<span className="text-[13px]">hr / week</span>}
                                        required={true}/>

                                </FormInputComponent>
                            </PositionEditInput>
                        ) : (position.isFixedFee() ? (
                            <div className="w-full flex-grow min-h-[71px] pt-3">
                                <p className="pb-2 font-semibold text-xs text-light-gray4 whitespace-nowrap">
                                    Total Budgeted
                                </p>
                                <p>{TB.formatCurrency(position.calculateBudgetBasedOnStrategy(), false)}</p>
                            </div>
                        ): (
                                position.isFixedRetainer() && (
                                    <div className="w-full flex-grow min-h-[71px] pt-3">
                                        <p className="pb-2 font-semibold text-xs text-light-gray4 whitespace-nowrap">
                                            Duration
                                        </p>
                                        <p>{position.payoutFrequencyLabel()}</p>
                                    </div>
                                ))
                        )}
                    </div>

                    <div className="flex xs:flex-col flex-1">

                        <PositionEditInput
                            label={project.isFixedFee() ? "Date of Last Deliverable" : (position.isFixedRetainer() ? "Est. End Date" : "End Date")}
                            isInvalid={!formModel.ended_at || !TB.moment(formModel.ended_at).isValid()}
                            formClass="position-dates"
                            displayValue={position.endedAt().format("MMM Do, YYYY")}
                            canEdit={project.isTimeMaterials() && canEdit && !editDisabled}
                            onEdit={() => onEditSection('timing_end')}
                            isEditing={activeEdit && activeEdit !== 'display_percent_markup'}
                        >
                            <PositionEndInput/>
                        </PositionEditInput>

                        {/*Allow total hours input*/}
                        {project.isTimeMaterials() ? (
                            <div className="w-full flex-grow min-h-[71px] pt-3">
                                <p className="pb-2 font-semibold text-xs text-light-gray4 whitespace-nowrap">
                                    Total Hours Budget
                                </p>
                                <p>{displayedTotalHours} hours</p>
                            </div>
                        ) : (
                            position.isFixedRetainer() && (
                                <div className="w-full flex-grow min-h-[71px] pt-3">
                                    <p className="pb-2 font-semibold text-xs text-light-gray4 whitespace-nowrap">
                                        Payout per {position.payout_frequency === 'weekly' ? 'week' : 'month'}
                                    </p>
                                    <p>
                                        {auth.canViewTalentPricing() ? (
                                            <>
                                                {position.isFixedRetainerRateBased() ? (
                                                    <span>
                                                        {TB.formatCurrency(proposedPayoutPerPeriod, false)} ({displayedHoursPerWeek} hrs/{position.payout_frequency === 'weekly' ? 'week' : 'month'})
                                                    </span>
                                                ) : (
                                                    <span>{TB.formatCurrency(proposedPayoutPerPeriod, false)}</span>
                                                )}
                                            </>
                                        ) : (
                                            <HideTalentPricingTooltip />
                                        )}
                                    </p>
                                </div>
                            )
                        )}
                    </div>

                    <div className="flex xs:flex-col basis-5/12">
                        <div className="p-3 bg-light-gray3 flex flex-col gap-6">
                            <div>
                                <p className="pb-2 font-semibold text-xs text-light-gray4 whitespace-nowrap">
                                    {project.isFixedFee() ? (
                                        project.isActive() ? 'Total Project Price*' : 'Total Estimated Project Price*'
                                    ) : (
                                        project.isActive() ? 'Total Resource Cost' : 'Total Estimated Resource Cost'
                                    )}
                                </p>
                                {auth.canViewTalentPricing() ? (
                                    <AnimatedNumber
                                        value={bulkCost}
                                        formatValue={(value) => (
                                            <span className="flex flex-row items-center gap-2.5">
                                                <span className="text-xs">{TB.formatCurrency(value, false)}</span>
                                                {project.isFixedFee() && (
                                                    <span className="text-xs font-medium">Fixed Fee</span>
                                                )}
                                                {position.isFixedRetainer() && position.isFixedRetainerRateBased() && (
                                                    <span className="text-xs">
                                                        ({displayedTotalHours} total hours)
                                                    </span>
                                                )}
                                            </span>
                                        )}
                                        duration={duration}
                                    />
                                ) : (
                                    <HideTalentPricingTooltip />
                                )}

                            </div>
                            {/*{auth?.canManageClients() && (*/}
                            {/*    <div>*/}
                            {/*        <PositionEditInput*/}
                            {/*            label="Default Talent Percent Markup"*/}
                            {/*            isEditing={activeEdit === 'display_percent_markup'}*/}
                            {/*            canEdit={canEdit && !editDisabled}*/}
                            {/*            onEdit={() => onEditSection('display_percent_markup')}*/}
                            {/*            displayValue={(*/}
                            {/*                <React.Fragment>*/}
                            {/*                    <span className="flex flex-row items-center gap-3">*/}
                            {/*                        {originalStandardMarkup !== formModel.display_percent_markup && (*/}
                            {/*                            <span className="text-danger line-through">{originalStandardMarkup}%</span>*/}
                            {/*                        )}*/}
                            {/*                        <span>{formModel.display_percent_markup}%</span>*/}
                            {/*                    </span>*/}
                            {/*                </React.Fragment>*/}
                            {/*            )}*/}
                            {/*            isInvalid={!formModel.display_percent_markup}*/}
                            {/*        >*/}
                            {/*            <div className="flex flex-row items-center">*/}
                            {/*                <div className="w-[130px]">*/}
                            {/*                    <FormInputComponent*/}
                            {/*                        fieldName="display_percent_markup"*/}
                            {/*                        value={formModel.display_percent_markup}*/}
                            {/*                        type="antd-input"*/}
                            {/*                        disabled={isDisabled}*/}
                            {/*                        placeholder="--"*/}
                            {/*                        min={0}*/}
                            {/*                        label={false}*/}
                            {/*                        required={true}*/}
                            {/*                    >*/}
                            {/*                        <InputNumber*/}
                            {/*                            controls={false}*/}
                            {/*                            className="w-full"*/}
                            {/*                            addonAfter="%"*/}
                            {/*                            min="0"*/}
                            {/*                            max="5000"*/}
                            {/*                            precision={6}*/}
                            {/*                            formatter={value => Number(value)}*/}
                            {/*                        />*/}
                            {/*                    </FormInputComponent>*/}
                            {/*                </div>*/}
                            {/*                {originalStandardMarkup !== formModel.display_percent_markup && (*/}
                            {/*                    <div className="mx-2 mb-4 flex-grow">*/}
                            {/*                        <button*/}
                            {/*                            type="button"*/}
                            {/*                            onClick={async () => {*/}
                            {/*                                await onFormChange({display_percent_markup: originalStandardMarkup});*/}
                            {/*                                return onSaveSection({*/}
                            {/*                                    display_percent_markup: originalStandardMarkup,*/}
                            {/*                                    percent_markup: Number(parseFloat(originalStandardMarkup) / 100)*/}
                            {/*                                })*/}
                            {/*                            }}*/}
                            {/*                            className="btn btn-sm btn-link mx-auto btn-link-danger">*/}
                            {/*                            <small>Reset to standard markup: {originalStandardMarkup}%</small>*/}
                            {/*                        </button>*/}
                            {/*                    </div>*/}
                            {/*                )}*/}
                            {/*            </div>*/}
                            {/*        </PositionEditInput>*/}

                            {/*    </div>*/}
                            {/*)}*/}
                        </div>

                        {(project.isTimeMaterials() || (position.isFixedRetainer() && position.isFixedRetainerRateBased())) && (
                            <div className="w-full flex-1 min-h-[71px] pt-3">
                                <div className="pb-2 flex flex-row items-center gap-1.5">
                                    {position.hasBeenFilled() ? (
                                        <p className="inline font-semibold text-xs text-info whitespace-nowrap">
                                            Assigned Bill Rate
                                        </p>
                                    ) : (
                                        position.target_rate ? (
                                            <p className="inline font-semibold text-xs text-danger whitespace-nowrap">
                                                Target Bill Rate
                                            </p>
                                        ) : (
                                            <p className="inline font-semibold text-xs text-light-gray4 whitespace-nowrap">
                                                Bill Rate *
                                            </p>
                                        )
                                    )}
                                </div>

                                <div>
                                    {auth.canViewTalentPricing() ? (
                                        <>
                                            <p>{hourlyRate} / hour</p>
                                            {!position.hasBeenFilled() && !position.target_rate && (
                                                <p className="whitespace-no-wrap text-xxs text-primary font-medium italic">
                                                    * Bill rate set based upon average rate on {laravel.platform_name}
                                                </p>
                                            )}
                                        </>
                                    ) : (
                                        <HideTalentPricingTooltip />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <PositionTimeline>
                    <div className={TB.mapClass("h-0 transition-all ease-in overflow-hidden", {
                        "h-[37px]": activeEdit
                    })}>
                        {canEdit && activeEdit && (
                            <div className="flex flex-row gap-2 items-center justify-end">
                                <button type="button" disabled={isDisabled}
                                        onClick={() => onEditSection(null)}
                                        className="btn btn-dark disabled:opacity-60">
                                    Cancel
                                </button>
                                <button type="submit"
                                        disabled={isDisabled}
                                        className="btn btn-primary disabled:opacity-60">
                                    Update
                                </button>
                            </div>
                        )}
                    </div>
                </PositionTimeline>
            </Form>

        </React.Fragment>
    )
}

const PositionContractHistoryTab = (props) => {
    const {auth} = useAuth();
    const {project, isLoading} = useProject();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedChangeOrder, setSelectedChangeOrder] = useState(null);
    const {position, isDisabled} = usePosition();

    return (
        <div>
            <table className="table">
                <thead>
                <tr>
                    <th className="border-0 text-left"/>
                    <th className="border-0 text-left">Start Date</th>
                    <th className="border-0 text-left">End Date</th>
                    <th className="border-0 text-left">Hours</th>
                    <th className="border-0 text-left">Project Estimate/Rate</th>
                    {auth.isClientConsultant() && (
                        <th className="border-0 text-left">Talent Cost/Rate</th>
                    )}
                    <th className="border-0 text-left"/>
                </tr>
                </thead>

                <tbody className="table-bordered">
                <tr>
                    <td className="border-0 flex justify-between items-center">
                        <span>
                            Original Contract
                        </span>
                        <span className="text-xs">
                            {TB.moment(project.started_at).format('M/D/YYYY')}
                        </span>
                    </td>
                    <td>
                        {position.startedAt().format('M/D/YYYY')}
                    </td>
                    <td>
                        {TB.moment(position.original_ended_at).format('M/D/YYYY') || 'N/A'}
                    </td>
                    <td>
                        {position.original_total_periods}
                    </td>
                    <td>
                        {TB.formatCurrency(position.original_contract_value, false)}
                        &nbsp;/&nbsp;
                        {TB.formatCurrency(Math.ceil(position.original_rate * (1 + (position.original_percent_markup || 0))))}
                    </td>
                    {auth.isClientConsultant() && (
                        <td>
                            {TB.formatCurrency(position.original_talent_cost, false)}
                            &nbsp;/&nbsp;
                            {TB.formatCurrency(Math.ceil(position.original_rate))}
                        </td>
                    )}

                    <td className="w-[100px]"/>
                </tr>

                {position.change_order_items?.length > 0 && TB.map(TB.orderBy(position.change_order_items, 'id', 'asc'), (co, i) => {
                    let lastCandidateSegment = TB.last(co.candidate_segments)
                    return (
                        <tr key={co.id} className="bg-gray-50">
                            <td>
                                <div className="flex justify-between items-center">
                                    <span>
                                        {typeLabels[co.type]}
                                    </span>
                                    <span className="text-xs">
                                        {TB.moment(co.effective_at).format('M/D/YYYY')}
                                    </span>
                                </div>
                            </td>
                            <td>
                                {lastCandidateSegment?.started_at ? TB.moment(lastCandidateSegment?.started_at).format('M/D/YYYY') : 'N/A'}
                            </td>
                            <td>
                                <p className={co.new_ended_at !== co.original_ended_at ? 'font-semibold' : ''}>
                                    {TB.moment(co.new_ended_at).format('M/D/YYYY')}
                                </p>
                            </td>
                            <td>
                                <p className={((co.new_total_periods - co.original_total_periods) !== 0) ? 'font-semibold' : ''}>
                                    {`${(co.new_total_periods - co.original_total_periods) >= 0 ? '+' : '-'}${Math.abs((co.new_total_periods - co.original_total_periods))}`}
                                </p>

                            </td>
                            <td>
                                <p>
                                    <span>
                                        {co.net_contract_value >= 0 ? '+' : '-'}
                                        {TB.formatCurrency(Math.abs(co.net_contract_value), false)}
                                    </span>
                                    &nbsp;/&nbsp;
                                    <span
                                        className={co.new_rate !== co.original_rate ? 'font-semibold' : ''}>
                                        {TB.formatCurrency(co.new_rate ? Math.ceil(co.new_rate * (1 + (position?.percent_markup || 0))) : Math.ceil(position.platform_rate), false)}
                                    </span>

                                    {/*<span className="ml-2 text-xs text-danger">*/}
                                    {/*    {TB.formatCurrency(Math.abs(co.new_contract_value), false)}*/}
                                    {/*</span>*/}
                                </p>
                            </td>

                            {auth.isClientConsultant() && (
                                <td>
                                    <p>
                                        <span>
                                            {co.net_talent_cost >= 0 ? '+' : '-'}
                                            {TB.formatCurrency(Math.abs(co.net_talent_cost), false)}
                                        </span>
                                        &nbsp;/&nbsp;
                                        <span
                                            className={co.new_rate !== co.original_rate ? 'font-semibold' : ''}>
                                            {TB.formatCurrency(co.new_rate ? Math.ceil(co.new_rate) : Math.ceil(co.new_platform_rate / (1 + (co?.new_percent_markup || 0))), false)}
                                        </span>

                                        {/*<span className="ml-2 text-xs text-danger">*/}
                                        {/*    {TB.formatCurrency(Math.abs(co.new_talent_cost), false)}*/}
                                        {/*</span>*/}
                                    </p>
                                </td>
                            )}

                            <td className="w-[100px]">
                                <button type="button" onClick={() => {
                                    setIsModalOpen(true);
                                    setSelectedChangeOrder(co)
                                }} className="btn btn-link-primary p-0 mx-auto">
                                    View
                                </button>
                            </td>
                        </tr>
                    )
                })}
                <tr className="bg-[#595959] text-white">
                    <td>
                        <div className="flex justify-between items-center">
                            <span>
                                Running Total
                            </span>
                            <span className="text-xs">
                                Today
                            </span>
                        </div>
                    </td>
                    <td>{TB.moment(position.started_at).format('M/D/YYYY')}</td>
                    <td>{TB.moment(position.ended_at).format('M/D/YYYY')}</td>
                    <td>{position.actual_total_periods}</td>
                    <td>
                        {TB.formatCurrency(position.contract_value, false)}
                        &nbsp;/&nbsp;
                        {TB.formatCurrency(Math.ceil(position.platform_rate), false)}
                    </td>

                    {auth.isClientConsultant() && (
                        <td>
                            {TB.formatCurrency(position.talent_cost, false)}
                            &nbsp;/&nbsp;
                            {TB.formatCurrency(Math.ceil(position.assigned_candidate?.job_candidate?.premarkup_rate), false)}
                        </td>
                    )}
                    <td/>
                </tr>
                </tbody>
            </table>

            <Drawer
                title={(
                    <div className="px-2 py-1.5">
                        <p>
                            &nbsp;
                            Change Order Item for Position:
                            &nbsp;
                            {position && (
                                <span className="font-semibold">{position?.title}</span>
                            )}
                        </p>
                    </div>
                )}
                width={1200}
                onClose={() => setIsModalOpen(false)}
                open={isModalOpen}
            >
                {selectedChangeOrder && TB.find(project.change_orders, order => TB.find(order?.items || [], ['id', selectedChangeOrder.id])) && (
                    <ChangeOrderItemView
                        isDisabled={isDisabled}
                        order={TB.find(project.change_orders, order => TB.find(order?.items || [], ['id', selectedChangeOrder.id]))}
                        onClose={() => setIsModalOpen(false)}/>
                )}
            </Drawer>
        </div>
    )
}
const PositionCostOverview = ({hideOverrideTools, canEdit, onEdit, isEditing, ...props}) => {

    const {auth} = useAuth();
    const {project, isLoading} = useProject();
    const isFixedFee = project.isFixedFee();

    const {
        position,
        deletePosition,
        isProjectUpdating
    } = usePositionForm();

    const isDisabled = isProjectUpdating || props.isDisabled;

    const canEditProjectAndPositions = auth?.canEditProjectAndPositions(project);
    const canAddRemoveProjectPositions = auth?.canAddRemoveProjectPositions(project);

    const editDisabled = !canEditProjectAndPositions || project.isActive() || project.is_on_hold || (project.hasRequestedSOW() && !auth.isClientConsultant());


    const candidate = useMemo(() => {
        return position.assignedCandidate();
    }, [position?.id, isDisabled, position?.is_filled]);

    const canUserOverrideTools = !hideOverrideTools && auth.canManageClients() && candidate;

    const OverviewWrapper = useMemo(() => {
        return canUserOverrideTools ? PositionOverrideProvider : React.Fragment
    }, [canUserOverrideTools]);

    const [activeEdit, setActiveEdit] = useState(null);

    const onSetActive = (section) => {
        if (canEdit && onEdit) {
            if (section && !isEditing) {
                onEdit(true)
            } else if (!section && isEditing) {
                onEdit(false)
            }
        }
        setActiveEdit(section)
    }
    return (
        <OverviewWrapper {...(canUserOverrideTools ? {positionId: position.id} : {})}>
            <div className={TB.mapClass("position-cost-row relative", {
                "opacity-50 pointer-events-none": isDisabled
            })}>
                <div className="candidate-info">

                    {candidate ? (
                        <UserImageCircle
                            user={candidate}
                            hideTooltip={true}
                            size={60} className="img-fluid img-grayscale"/>
                    ) : (
                        <i className="img-fluid fas fa-user-circle text-6xl"/>
                    )}

                    <div className="flex flex-col justify-center flex-grow gap-3">
                        <div className="flex flex-row justify-start items-center gap-4">
                            <Tooltip
                                placement="top"
                                color="#000"
                                title={(candidate && auth.canManageClients()) ? candidate.displayName(auth) : null}
                            >
                                <p className="text-xl text-primary-100">
                                    {candidate ? candidate.firstNamePlusInitial() : 'TBD'}
                                </p>
                            </Tooltip>

                            {!editDisabled && !candidate && deletePosition && !activeEdit && (
                                <button type="button" disabled={isDisabled}
                                        onClick={() => {
                                            if (!auth?.canAddRemoveProjectPositions(props.project)) {
                                                return TB.showPermissionError();
                                            }
                                            return deletePosition()
                                        }}
                                        className={TB.mapClass("btn btn-link-danger py-0", {
                                            "disabled-edit-link": !canAddRemoveProjectPositions
                                        })}>
                                    {!canAddRemoveProjectPositions && (<i className="fa fa-lock ml-1"/>)}

                                    Delete Position
                                </button>
                            )}

                            {candidate?.hasMatching() && (
                                <div className="flex flex-col">
                                    <div className="flex flex-row items-center">
                                        <SimpleCircleProgress
                                            stroke={18}
                                            width="16px"
                                            className="mr-2"
                                            value={candidate.overallMatchPercent()}/>

                                        <AnimatedNumber
                                            className="font-semibold text-xs mb-0"
                                            value={candidate.overallMatchPercent()}
                                            formatValue={(value) => `${TB.round(value)}% Total Match`}
                                            duration={1000}/>
                                    </div>
                                </div>
                            )}
                        </div>

                        {auth.isClientConsultant() && candidate && candidate?.email && candidate?.phone && (
                            <div>
                                <p className="flex gap-2">
                                    <i className="fas fa-envelope text-base" />
                                    <a href={`mailto:${candidate.email}`}>
                                        {candidate.email}
                                    </a>
                                </p>
                                <p className="flex gap-2">
                                    <i className="fas fa-phone text-base" />
                                    <a href={`tel:${candidate.phone}`}>
                                        {candidate.phone}
                                    </a>
                                </p>
                            </div>
                        )}


                        <div className="flex flex-col items-start gap-1.5">
                            <p>
                                <b>Position:</b> {position.title}
                            </p>
                            <p>
                                <b>Contract Type:</b>&nbsp;
                                <span className={position.isFixedFee() ? "text-warning-darker" : (project.isFixedRetainer() ? "text-danger" : "text-info")}>
                                    {position.contractTypeName()}
                                    {!!position.isFixedRetainer() && (
                                        <span className="capitalize"> - {position.payout_frequency}</span>
                                    )}
                                </span>
                            </p>
                        </div>
                    </div>
                    {canUserOverrideTools && (
                        <div className="ml-auto flex-grow max-w-[800px]">
                            <ManageTalentContractForPosition/>
                        </div>
                    )}
                </div>

                {(project.isActive() || (!project.isTimeMaterials() && canUserOverrideTools)) ? (
                    <Tabs
                        defaultActiveKey="details"
                        destroyInactiveTabPane={true}
                        items={[
                            ...((project.isFixedFee() && canUserOverrideTools) ? [
                                {
                                    label: 'Details',
                                    key: 'details',
                                    children: <PositionCandidateCostTab canEdit={true} activeEdit={activeEdit}
                                                                        setActiveEdit={onSetActive} {...props}/>
                                },
                                {
                                    label: 'Scoping',
                                    key: 'scope',
                                    children: <PositionCandidateCostOverrideTab/>
                                },
                            ] : [
                                {
                                    label: 'Details',
                                    key: 'details',
                                    children: (canUserOverrideTools ? (
                                        <PositionCandidateCostOverrideTab/>
                                    ) : (
                                        <PositionCandidateCostTab canEdit={canEdit} activeEdit={activeEdit}
                                                                  setActiveEdit={onSetActive} {...props}/>
                                    ))
                                }
                            ]),

                            ...(project.isActive() ? [
                                {
                                    label: 'Contract History',
                                    key: 'contract_history',
                                    children: (
                                        <PositionContractHistoryTab/>
                                    )
                                },
                            ] : []),

                            // {
                            //     label: 'Expenses',
                            //     key: 'expenses',
                            //     children: (
                            //
                            //         <PositionContractExpensesTable/>
                            //     )
                            // },

                        ]}
                    />
                ) : (canUserOverrideTools ? (
                    <PositionCandidateCostOverrideTab/>
                ) : (
                    <PositionCandidateCostTab canEdit={canEdit} activeEdit={activeEdit}
                                              setActiveEdit={onSetActive} {...props}/>
                ))}


                {isDisabled && (
                    <div className="absolute flex flex-col items-center justify-center w-full h-full">
                        <LoadingSpinner size="lg"/>
                    </div>
                )}
            </div>
        </OverviewWrapper>
    )
}

PositionCostOverview.propTypes = {};


export default PositionCostOverview;